import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { alertsApi, vehiclesApi, AlertFilters } from '@/services/api';
import { useSocketEvent } from '@/services/socket';
import OrganizationFilter from '@/components/OrganizationFilter';
import { useAuthStore } from '@/stores/authStore';
import { useSettingsStore, formatTime } from '@/stores/settingsStore';
import {
  BellAlertIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  MapPinIcon,
  ClockIcon,
  TruckIcon,
  BoltIcon,
  TrashIcon,
  EyeIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  FireIcon,
  BellIcon,
  BellSlashIcon,
} from '@heroicons/react/24/outline';
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, AreaChart, Area,
  LineChart, Line,
} from 'recharts';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ar';
import clsx from 'clsx';
import {
  ALERT_TYPE_META,
  SEVERITY_META,
  isTamperAlert,
} from '@/constants/alertTypes';

dayjs.extend(relativeTime);
dayjs.locale('ar');

const PAGE_SIZE = 100;

interface AlertRow {
  id: string;
  type: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  message: string;
  messageAr?: string | null;
  vehicleId: string | null;
  vehicle?: { id: string; plateNumber: string; vehicleType: string } | null;
  latitude?: string | number | null;
  longitude?: string | number | null;
  metadata?: Record<string, any> | null;
  isRead: boolean;
  isAcknowledged: boolean;
  acknowledgedAt?: string | null;
  isResolved: boolean;
  resolvedAt?: string | null;
  createdAt: string;
}

/**
 * Must stay in step with the `alert_type` enum. The six IO-derived types added
 * with the extend-alert-types migration were missing here, so they rendered as
 * raw English identifiers and could not be picked in the type filter.
 */
// Type/severity descriptions come from the shared table so this page and the
// dashboard cannot drift apart (they already had: `sos` and both org-zone
// types were missing here and rendered as raw identifiers).
const alertTypeConfig = ALERT_TYPE_META;
const severityConfig = SEVERITY_META;

const typeName = (t: string) => alertTypeConfig[t]?.name || t;

export default function Alerts() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { timezone } = useSettingsStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedOrg, setSelectedOrg] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<AlertRow | null>(null);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [page, setPage] = useState(0);
  const [dateRange, setDateRange] = useState({
    startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
  });

  // Only ministry_admin and above may delete; the buttons are hidden for
  // everyone else rather than failing with a 403 after the confirm prompt.
  const canBulkDelete = user?.role === 'super_admin' || user?.role === 'ministry_admin';

  const filters: AlertFilters = useMemo(() => ({
    severity: selectedSeverity || undefined,
    type: selectedType || undefined,
    vehicleId: selectedVehicle || undefined,
    status: (selectedStatus || undefined) as AlertFilters['status'],
    organizationId: selectedOrg || undefined,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  }), [selectedSeverity, selectedType, selectedVehicle, selectedStatus, selectedOrg, dateRange]);

  // Any filter change invalidates the current page offset.
  useEffect(() => { setPage(0); }, [filters]);

  const alertsQ = useQuery({
    queryKey: ['alerts', filters, page],
    queryFn: async () => {
      const r = await alertsApi.getAll({ ...filters, limit: PAGE_SIZE, offset: page * PAGE_SIZE });
      return {
        items: (r.data.data ?? []) as AlertRow[],
        pagination: r.data.pagination ?? { total: 0, hasMore: false },
      };
    },
    refetchInterval: autoRefresh ? 15_000 : false,
    placeholderData: (prev) => prev,
  });

  // Aggregates come from the server across the whole filtered set. Deriving
  // them in the browser from one page silently under-reported every figure
  // once a fleet passed the page size, and bucketed hours in the viewer's
  // timezone instead of Tripoli's.
  const statsQ = useQuery({
    queryKey: ['alerts-stats', filters],
    queryFn: async () => (await alertsApi.getStats(filters)).data.data,
    refetchInterval: autoRefresh ? 30_000 : false,
  });

  const vehiclesQ = useQuery({
    queryKey: ['vehicles'],
    queryFn: async () => (await vehiclesApi.getAll()).data.data,
    staleTime: 5 * 60_000,
  });

  const alerts = alertsQ.data?.items ?? [];
  const total = alertsQ.data?.pagination?.total ?? 0;
  const stats = statsQ.data;

  const refreshAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['alerts'] });
    queryClient.invalidateQueries({ queryKey: ['alerts-stats'] });
    // The navbar badge reads this key; without it the count went stale after
    // acknowledging or resolving from this page.
    queryClient.invalidateQueries({ queryKey: ['unread-count'] });
  }, [queryClient]);

  /**
   * Live updates, coalesced.
   *
   * `alert:new` is broadcast to every client for every alert in the system,
   * and the server's offline sweep runs once a minute over the whole fleet —
   * so one sweep can emit an alert per vehicle within a second. Refreshing on
   * each event issued two requests per alert, which on a large fleet was a
   * burst of dozens per second and the main way this page exhausted the rate
   * limit. The dashboard already coalesced its handlers this way; this page
   * did not.
   */
  const alertBurstTimer = useRef<number | null>(null);
  const onAlertBurst = useCallback(() => {
    if (alertBurstTimer.current) return;
    alertBurstTimer.current = window.setTimeout(() => {
      alertBurstTimer.current = null;
      refreshAll();
    }, 2000);
  }, [refreshAll]);

  useEffect(() => () => {
    if (alertBurstTimer.current) clearTimeout(alertBurstTimer.current);
  }, []);

  useSocketEvent('alert:new', onAlertBurst);

  const mutationOpts = (successMsg: string) => ({
    onSuccess: () => { refreshAll(); toast.success(successMsg); },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'تعذّر تنفيذ العملية');
    },
  });

  const acknowledgeMutation = useMutation({
    mutationFn: (id: string) => alertsApi.acknowledge(id),
    ...mutationOpts('تم تأكيد الاستلام'),
  });
  const resolveMutation = useMutation({
    mutationFn: (id: string) => alertsApi.resolve(id),
    ...mutationOpts('تم وضع علامة الحل'),
  });
  const markAllReadMutation = useMutation({
    mutationFn: () => alertsApi.markAllRead(),
    onSuccess: (r: any) => {
      refreshAll();
      toast.success(r?.data?.message || 'تم تعليم الكل كمقروء');
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'تعذّر التنفيذ'),
  });

  // Marking one read is a side effect of opening it — silent, no toast, and
  // it must not invalidate the list mid-scroll.
  const markAsReadMutation = useMutation({
    mutationFn: (id: string) => alertsApi.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts-stats'] });
      queryClient.invalidateQueries({ queryKey: ['unread-count'] });
    },
  });

  // Deletes exactly the filtered set the operator is looking at. The old
  // button promised "delete these N" but sent confirm=true with no filters,
  // wiping the entire alert history regardless of what was on screen.
  const deleteFilteredMutation = useMutation({
    mutationFn: async () => {
      const hasFilter = Boolean(
        filters.severity || filters.type || filters.vehicleId || filters.status,
      );
      return alertsApi.deleteBulk({ ...filters, confirm: hasFilter ? undefined : true });
    },
    onSuccess: (r: any) => {
      refreshAll();
      setPage(0);
      toast.success(r?.data?.message || 'تم الحذف');
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'تعذّر الحذف'),
  });

  const confirmDelete = async () => {
    try {
      // Ask the server how many rows actually match before quoting a number.
      const probe = await alertsApi.deleteBulk({ ...filters, dryRun: true, confirm: true });
      const count = probe.data?.matchedCount ?? 0;
      if (count === 0) { toast('لا توجد تنبيهات مطابقة للحذف'); return; }
      if (window.confirm(
        `سيتم حذف ${count} تنبيهاً ضمن الفلاتر الحالية (${dateRange.startDate} إلى ${dateRange.endDate}).\n`
        + 'لا يمكن التراجع عن هذه العملية. هل تريد المتابعة؟',
      )) {
        deleteFilteredMutation.mutate();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'تعذّر حساب عدد التنبيهات');
    }
  };

  // ── Critical-alert sound ─────────────────────────────────────────────
  // Only fires for ids not seen before. The previous effect depended on the
  // whole array, which React Query replaces on every poll, so a single unread
  // critical alert replayed the sound every 10 seconds indefinitely.
  const seenCriticalIds = useRef<Set<string> | null>(null);
  useEffect(() => {
    const critical = alerts.filter((a) => a.severity === 'critical' && !a.isRead);
    if (seenCriticalIds.current === null) {
      // First load is a baseline, not an event — don't alarm on history.
      seenCriticalIds.current = new Set(critical.map((a) => a.id));
      return;
    }
    const fresh = critical.filter((a) => !seenCriticalIds.current!.has(a.id));
    fresh.forEach((a) => seenCriticalIds.current!.add(a.id));
    if (fresh.length && soundEnabled) {
      new Audio('/alert-sound.mp3').play().catch(() => {});
    }
  }, [alerts, soundEnabled]);

  // Client-side text search over the current page only; everything else is
  // filtered server-side.
  const visibleAlerts = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const matched = !q ? alerts : alerts.filter((a) =>
      a.message?.toLowerCase().includes(q)
      || a.messageAr?.toLowerCase().includes(q)
      || a.vehicle?.plateNumber?.toLowerCase().includes(q)
      || typeName(a.type).toLowerCase().includes(q));

    // Unresolved tampering (device unplugged, power cut, crash) sorts above
    // everything else. Done here rather than in the query so the server's
    // ordering — and therefore pagination — stays chronological.
    const urgent = (a: AlertRow) => isTamperAlert(a.type) && !a.isResolved;
    return [...matched].sort((x, y) => Number(urgent(y)) - Number(urgent(x)));
  }, [alerts, searchTerm]);

  const severityData = useMemo(() => ([
    { name: 'حرج', value: stats?.critical ?? 0, color: severityConfig.critical.chart },
    { name: 'عالي', value: stats?.high ?? 0, color: severityConfig.high.chart },
    { name: 'متوسط', value: stats?.medium ?? 0, color: severityConfig.medium.chart },
    { name: 'منخفض', value: stats?.low ?? 0, color: severityConfig.low.chart },
  ].filter((d) => d.value > 0)), [stats]);

  const typeData = useMemo(
    () => (stats?.byType ?? []).map((t: any) => ({ name: typeName(t.type), value: t.count })),
    [stats],
  );

  const dailyData = useMemo(() => (stats?.daily ?? []).map((d: any) => ({
    ...d,
    label: dayjs(d.date).format('DD/MM'),
  })), [stats]);

  // Today vs yesterday, taken from the server-side daily series.
  const trend = useMemo(() => {
    const series = stats?.daily ?? [];
    if (series.length < 2) return 0;
    const today = series[series.length - 1]?.total ?? 0;
    const yesterday = series[series.length - 2]?.total ?? 0;
    if (!yesterday) return today > 0 ? 100 : 0;
    return Math.round(((today - yesterday) / yesterday) * 100);
  }, [stats]);

  // ── Export ───────────────────────────────────────────────────────────
  const download = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const statusLabel = (a: AlertRow) =>
    a.isResolved ? 'تم حله' : a.isAcknowledged ? 'تم الاستلام' : 'جديد';

  const exportToCSV = useCallback(() => {
    if (!visibleAlerts.length) { toast('لا توجد تنبيهات للتصدير'); return; }
    const headers = ['التاريخ', 'النوع', 'الخطورة', 'المركبة', 'الرسالة', 'الحالة'];
    // Quote every cell: alert messages routinely contain commas
    // ("Speed limit exceeded: 155 km/h"), which shifted every later column.
    const esc = (c: any) => `"${String(c ?? '').replace(/"/g, '""')}"`;
    const rows = visibleAlerts.map((a) => [
      formatTime(a.createdAt, timezone, 'YYYY-MM-DD HH:mm'),
      typeName(a.type),
      severityConfig[a.severity]?.name || a.severity,
      a.vehicle?.plateNumber || '-',
      a.messageAr || a.message,
      statusLabel(a),
    ]);
    const csv = [headers.map(esc).join(','), ...rows.map((r) => r.map(esc).join(','))].join('\r\n');
    download(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }),
      `alerts_${dateRange.startDate}_${dateRange.endDate}.csv`);
  }, [visibleAlerts, timezone, dateRange]);

  const exportToJSON = useCallback(() => {
    if (!visibleAlerts.length) { toast('لا توجد تنبيهات للتصدير'); return; }
    download(
      new Blob([JSON.stringify({
        exportedAt: new Date().toISOString(), filters, stats, alerts: visibleAlerts,
      }, null, 2)], { type: 'application/json' }),
      `alerts_${dateRange.startDate}_${dateRange.endDate}.json`,
    );
  }, [visibleAlerts, filters, stats, dateRange]);

  const resetFilters = () => {
    setSelectedSeverity(''); setSelectedType('');
    setSelectedVehicle(''); setSelectedStatus(''); setSelectedOrg(''); setSearchTerm('');
  };
  const activeFilterCount = [selectedSeverity, selectedType, selectedVehicle, selectedStatus, selectedOrg]
    .filter(Boolean).length;

  const tooltipStyle = { contentStyle: { direction: 'rtl' as const, borderRadius: 8, fontSize: 13 } };
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const ChartCard = ({ title, icon, hasData, height = 250, children }: {
    title: string; icon: React.ReactNode; hasData: boolean; height?: number; children: React.ReactNode;
  }) => (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title flex items-center gap-2">{icon}{title}</h3>
      </div>
      <div className="card-body">
        {statsQ.isLoading ? (
          <div className="flex items-center justify-center" style={{ height }}>
            <div className="spinner w-8 h-8" />
          </div>
        ) : statsQ.error ? (
          <div className="flex flex-col items-center justify-center gap-2 text-center" style={{ height }}>
            <ExclamationTriangleIcon className="w-9 h-9 text-warning-400" />
            <p className="text-sm text-gray-600">
              {(statsQ.error as any)?.response?.data?.message || 'تعذّر تحميل الإحصائيات'}
            </p>
          </div>
        ) : !hasData ? (
          <div className="flex items-center justify-center text-gray-400 text-sm" style={{ height }}>
            لا توجد بيانات في هذه الفترة
          </div>
        ) : children}
      </div>
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BellAlertIcon className="w-6 h-6 sm:w-7 sm:h-7 text-emergency-500" />
            مركز التنبيهات
          </h1>
          <p className="text-sm text-gray-600 mt-1 flex items-center gap-2 flex-wrap">
            <span dir="ltr">{dateRange.startDate} → {dateRange.endDate}</span>
            {activeFilterCount > 0 && (
              <span className="badge badge-info">{activeFilterCount} فلتر نشط</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={clsx('btn btn-outline flex items-center gap-2 text-sm',
              soundEnabled ? 'text-success-600' : 'text-gray-400')}
            title={soundEnabled ? 'إيقاف صوت التنبيهات الحرجة' : 'تفعيل صوت التنبيهات الحرجة'}
          >
            {soundEnabled ? <SpeakerWaveIcon className="w-5 h-5" /> : <SpeakerXMarkIcon className="w-5 h-5" />}
          </button>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={clsx('btn btn-outline flex items-center gap-2 text-sm',
              autoRefresh ? 'text-success-600' : 'text-gray-400')}
            title={autoRefresh ? 'إيقاف التحديث التلقائي' : 'تفعيل التحديث التلقائي'}
          >
            {autoRefresh ? <BellIcon className="w-5 h-5" /> : <BellSlashIcon className="w-5 h-5" />}
          </button>
          <button onClick={refreshAll} disabled={alertsQ.isFetching}
                  className="btn btn-outline flex items-center gap-2 text-sm disabled:opacity-50">
            <ArrowPathIcon className={clsx('w-5 h-5', alertsQ.isFetching && 'animate-spin')} />
            <span className="hidden sm:inline">تحديث</span>
          </button>
          <button onClick={() => markAllReadMutation.mutate()}
                  disabled={markAllReadMutation.isPending || !stats?.unread}
                  className="btn btn-outline flex items-center gap-2 text-sm text-primary-600 disabled:opacity-50">
            <CheckCircleIcon className="w-5 h-5" />
            <span className="hidden sm:inline">قراءة الكل</span>
          </button>
          {canBulkDelete && (
            <button onClick={confirmDelete} disabled={deleteFilteredMutation.isPending}
                    className="btn btn-outline flex items-center gap-2 text-sm text-red-600 disabled:opacity-50"
                    title="حذف التنبيهات المطابقة للفلاتر الحالية">
              <TrashIcon className="w-5 h-5" />
              <span className="hidden sm:inline">حذف المطابق</span>
            </button>
          )}
          <button onClick={() => setShowFilters(!showFilters)}
                  className={clsx('btn btn-outline flex items-center gap-2 text-sm',
                    showFilters && 'bg-primary-50 border-primary-300')}>
            <FunnelIcon className="w-5 h-5" />
            <span className="hidden sm:inline">الفلاتر</span>
          </button>
          <button onClick={exportToCSV} className="btn btn-outline flex items-center gap-2 text-sm">
            <ArrowDownTrayIcon className="w-5 h-5" />CSV
          </button>
          <button onClick={exportToJSON} className="btn btn-outline flex items-center gap-2 text-sm">
            <ChartBarIcon className="w-5 h-5" />JSON
          </button>
        </div>
      </div>

      {/* Stat cards — all server-computed over the full filtered set */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
        <div className="card bg-gradient-to-br from-gray-500 to-gray-600 text-white">
          <div className="card-body py-3 sm:py-4">
            <p className="text-gray-200 text-xs sm:text-sm">الإجمالي</p>
            <p className="text-xl sm:text-2xl font-bold">{stats?.total ?? 0}</p>
            <div className="flex items-center gap-1 text-xs">
              {trend > 0 ? (
                <><ArrowTrendingUpIcon className="w-4 h-4 text-red-300" />
                  <span className="text-red-300">+{trend}% اليوم</span></>
              ) : trend < 0 ? (
                <><ArrowTrendingDownIcon className="w-4 h-4 text-green-300" />
                  <span className="text-green-300">{trend}% اليوم</span></>
              ) : <span className="text-gray-300">مستقر</span>}
            </div>
          </div>
        </div>
        {[
          { l: 'حرج', v: stats?.critical, icon: <FireIcon className="w-4 h-4" />, c: 'from-emergency-500 to-emergency-600' },
          { l: 'عالي', v: stats?.high, icon: <ExclamationTriangleIcon className="w-4 h-4" />, c: 'from-orange-500 to-orange-600' },
          { l: 'غير مقروء', v: stats?.unread, icon: <BellAlertIcon className="w-4 h-4" />, c: 'from-warning-500 to-warning-600' },
          { l: 'تم حله', v: stats?.resolved, icon: <CheckCircleIcon className="w-4 h-4" />, c: 'from-success-500 to-success-600' },
        ].map((s) => (
          <div key={s.l} className={`card bg-gradient-to-br ${s.c} text-white`}>
            <div className="card-body py-3 sm:py-4">
              <p className="text-white/80 text-xs sm:text-sm flex items-center gap-1">{s.icon}{s.l}</p>
              <p className="text-xl sm:text-2xl font-bold">{s.v ?? 0}</p>
            </div>
          </div>
        ))}
        <div className="card bg-gradient-to-br from-primary-500 to-primary-600 text-white">
          <div className="card-body py-3 sm:py-4">
            <p className="text-primary-100 text-xs sm:text-sm">معدل الحل</p>
            <p className="text-xl sm:text-2xl font-bold">{stats?.resolutionRate ?? 0}%</p>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="التوزيع حسب الخطورة" hasData={severityData.length > 0}
                   icon={<FireIcon className="w-5 h-5 text-emergency-500" />}>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={severityData} cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                   dataKey="value" nameKey="name"
                   label={({ name, value }) => `${name}: ${value}`}>
                {severityData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip {...tooltipStyle} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="التوزيع الساعي (توقيت طرابلس)"
                   hasData={(stats?.hourly ?? []).some((h: any) => h.count > 0)}
                   icon={<ClockIcon className="w-5 h-5 text-primary-500" />}>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={stats?.hourly ?? []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" interval={2} fontSize={11} />
              <YAxis orientation="right" fontSize={12} allowDecimals={false} />
              <Tooltip {...tooltipStyle} />
              <Area type="monotone" dataKey="count" name="التنبيهات" fill="#3b82f6"
                    fillOpacity={0.5} stroke="#3b82f6" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="الاتجاه اليومي" hasData={dailyData.some((d: any) => d.total > 0)}
                   icon={<ArrowTrendingUpIcon className="w-5 h-5 text-success-500" />}>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" />
              {/* Keyed on the date, not an English weekday abbreviation —
                  which repeated once a range exceeded seven days. */}
              <XAxis dataKey="label" fontSize={12} />
              <YAxis orientation="right" fontSize={12} allowDecimals={false} />
              <Tooltip {...tooltipStyle} />
              <Legend />
              <Line type="monotone" dataKey="total" name="الإجمالي" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="critical" name="الحرجة" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="resolved" name="تم حلها" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="التنبيهات حسب النوع" hasData={typeData.length > 0}
                   height={Math.max(250, typeData.length * 30)}
                   icon={<BoltIcon className="w-5 h-5 text-warning-500" />}>
          <ResponsiveContainer width="100%" height={Math.max(250, typeData.length * 30)}>
            <BarChart data={typeData} layout="vertical" margin={{ right: 110, left: 5 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" fontSize={12} allowDecimals={false} />
              <YAxis dataKey="name" type="category" orientation="right" width={110} fontSize={12} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="value" name="العدد" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="card">
          <div className="card-body">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">من تاريخ</label>
                <input type="date" value={dateRange.startDate} max={dateRange.endDate} className="input"
                       onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">إلى تاريخ</label>
                <input type="date" value={dateRange.endDate} min={dateRange.startDate}
                       max={dayjs().format('YYYY-MM-DD')} className="input"
                       onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الخطورة</label>
                <select value={selectedSeverity} className="input"
                        onChange={(e) => setSelectedSeverity(e.target.value)}>
                  <option value="">الكل</option>
                  {Object.entries(severityConfig).map(([k, c]) => (
                    <option key={k} value={k}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">النوع</label>
                <select value={selectedType} className="input"
                        onChange={(e) => setSelectedType(e.target.value)}>
                  <option value="">الكل</option>
                  {Object.entries(alertTypeConfig).map(([k, c]) => (
                    <option key={k} value={k}>{c.name}</option>
                  ))}
                </select>
              </div>
              <OrganizationFilter value={selectedOrg} onChange={setSelectedOrg} showCounts />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">المركبة</label>
                <select value={selectedVehicle} className="input"
                        onChange={(e) => setSelectedVehicle(e.target.value)}>
                  <option value="">الكل</option>
                  {Array.isArray(vehiclesQ.data) && vehiclesQ.data.map((v: any) => (
                    <option key={v.id} value={v.id}>{v.plateNumber}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الحالة</label>
                {/* Sends `status`, which the server understands. The old page
                    sent `isResolved`, so picking "تم الاستلام" filtered nothing. */}
                <select value={selectedStatus} className="input"
                        onChange={(e) => setSelectedStatus(e.target.value)}>
                  <option value="">الكل</option>
                  <option value="active">نشط (لم يُستلم)</option>
                  <option value="acknowledged">تم الاستلام</option>
                  <option value="resolved">تم حله</option>
                  <option value="unread">غير مقروء</option>
                </select>
              </div>
            </div>
            {activeFilterCount > 0 && (
              <div className="mt-4 flex justify-end">
                <button onClick={resetFilters} className="btn btn-sm btn-outline">
                  <XMarkIcon className="w-4 h-4 ml-1" />مسح الفلاتر
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="card">
        <div className="card-body">
          <div className="relative">
            <MagnifyingGlassIcon className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" value={searchTerm} className="input pr-10 w-full"
                   placeholder="بحث في تنبيهات الصفحة الحالية..."
                   onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
        </div>
      </div>

      {/* List */}
      <div className="card">
        <div className="card-header flex items-center justify-between gap-2 flex-wrap">
          <h3 className="card-title">
            قائمة التنبيهات
            <span className="text-gray-500 font-normal text-sm mr-2">
              {total > 0
                ? `${page * PAGE_SIZE + 1}–${page * PAGE_SIZE + alerts.length} من ${total}`
                : '0'}
            </span>
          </h3>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                      className="btn btn-sm btn-outline disabled:opacity-40">السابق</button>
              <span className="text-sm text-gray-600">{page + 1} / {totalPages}</span>
              <button onClick={() => setPage((p) => p + 1)}
                      disabled={!alertsQ.data?.pagination?.hasMore}
                      className="btn btn-sm btn-outline disabled:opacity-40">التالي</button>
            </div>
          )}
        </div>
        <div className="card-body p-0">
          {alertsQ.isLoading ? (
            <div className="flex items-center justify-center h-64"><div className="spinner w-10 h-10" /></div>
          ) : alertsQ.error ? (
            <div className="p-8 text-center">
              <ExclamationTriangleIcon className="w-12 h-12 mx-auto text-warning-400 mb-2" />
              <p className="text-gray-700 font-medium">تعذّر تحميل التنبيهات</p>
              <p className="text-sm text-gray-500 mt-1">
                {(alertsQ.error as any)?.response?.data?.message || (alertsQ.error as any)?.message}
              </p>
              <button onClick={refreshAll} className="btn btn-outline btn-sm mt-4">إعادة المحاولة</button>
            </div>
          ) : visibleAlerts.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <BellAlertIcon className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p>لا توجد تنبيهات مطابقة</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {visibleAlerts.map((alert) => (
                <div key={alert.id}
                     className={clsx('p-3 sm:p-4 transition-colors cursor-pointer',
                       // Tampering and crashes must not read as ordinary rows:
                       // a solid tint and a thick edge, so they stay findable
                       // while scrolling a long list. Branching on the whole
                       // background here rather than layering it over the
                       // unread tint — two background utilities on one element
                       // are resolved by stylesheet order, not by the order
                       // they are listed, so the unread tint silently won.
                       isTamperAlert(alert.type) && !alert.isResolved
                         ? 'bg-emergency-50 border-r-4 border-r-emergency-600 hover:bg-emergency-100'
                         : clsx(
                             !alert.isRead && 'bg-primary-50/50',
                             alert.severity === 'critical' && !alert.isResolved
                               && 'border-r-4 border-r-emergency-500',
                             'hover:bg-gray-50',
                           ))}
                     onClick={() => {
                       setSelectedAlert(alert);
                       if (!alert.isRead) markAsReadMutation.mutate(alert.id);
                     }}>
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className={clsx('p-2 rounded-lg shrink-0',
                      isTamperAlert(alert.type) && !alert.isResolved
                        ? 'bg-emergency-600 text-white'
                        : severityConfig[alert.severity]?.bgColor)}>
                      <span className={isTamperAlert(alert.type) && !alert.isResolved
                        ? 'text-white'
                        : (alertTypeConfig[alert.type]?.color || 'text-gray-500')}>
                        {alertTypeConfig[alert.type]?.icon || <BellAlertIcon className="w-5 h-5" />}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={clsx('text-sm font-medium', severityConfig[alert.severity]?.color)}>
                          {typeName(alert.type)}
                        </span>
                        <span className={clsx('badge text-xs',
                          alert.severity === 'critical' ? 'badge-error'
                            : alert.severity === 'high' ? 'badge-warning'
                              : alert.severity === 'medium' ? 'badge-info' : 'badge-success')}>
                          {severityConfig[alert.severity]?.name}
                        </span>
                        {isTamperAlert(alert.type) && !alert.isResolved && (
                          <span className="badge bg-emergency-600 text-white text-xs">يتطلب تدخلاً</span>
                        )}
                        {!alert.isRead && <span className="w-2 h-2 bg-primary-500 rounded-full" />}
                      </div>
                      <p className="text-gray-700 text-sm mb-1 break-words">
                        {alert.messageAr || alert.message}
                      </p>
                      <div className="flex items-center gap-3 sm:gap-4 text-xs text-gray-500 flex-wrap">
                        <span className="flex items-center gap-1">
                          <TruckIcon className="w-4 h-4" />{alert.vehicle?.plateNumber || '—'}
                        </span>
                        <span className="flex items-center gap-1" title={formatTime(alert.createdAt, timezone, 'YYYY-MM-DD HH:mm:ss')}>
                          <ClockIcon className="w-4 h-4" />{dayjs(alert.createdAt).fromNow()}
                        </span>
                        {alert.isResolved ? (
                          <span className="flex items-center gap-1 text-success-600">
                            <CheckCircleIcon className="w-4 h-4" />تم الحل
                          </span>
                        ) : alert.isAcknowledged ? (
                          <span className="flex items-center gap-1 text-primary-600">
                            <EyeIcon className="w-4 h-4" />تم الاستلام
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {!alert.isAcknowledged && !alert.isResolved && (
                        <button onClick={(e) => { e.stopPropagation(); acknowledgeMutation.mutate(alert.id); }}
                                disabled={acknowledgeMutation.isPending}
                                className="btn btn-sm btn-outline text-primary-600 disabled:opacity-50"
                                title="تأكيد الاستلام">
                          <EyeIcon className="w-4 h-4" />
                        </button>
                      )}
                      {!alert.isResolved && (
                        <button onClick={(e) => { e.stopPropagation(); resolveMutation.mutate(alert.id); }}
                                disabled={resolveMutation.isPending}
                                className="btn btn-sm btn-outline text-success-600 disabled:opacity-50"
                                title="وضع علامة تم الحل">
                          <CheckCircleIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail modal */}
      {selectedAlert && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
             onClick={() => setSelectedAlert(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className={clsx('p-4 border-b flex items-center justify-between',
              severityConfig[selectedAlert.severity]?.bgColor)}>
              <div className="flex items-center gap-3">
                <span className={alertTypeConfig[selectedAlert.type]?.color || 'text-gray-500'}>
                  {alertTypeConfig[selectedAlert.type]?.icon || <BellAlertIcon className="w-5 h-5" />}
                </span>
                <div>
                  <h3 className="font-bold text-gray-900">{typeName(selectedAlert.type)}</h3>
                  <p className={clsx('text-sm', severityConfig[selectedAlert.severity]?.color)}>
                    {severityConfig[selectedAlert.severity]?.name}
                  </p>
                </div>
              </div>
              <button onClick={() => setSelectedAlert(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-sm text-gray-500">الرسالة</label>
                <p className="text-gray-900">{selectedAlert.messageAr || selectedAlert.message}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-500">المركبة</label>
                  <p className="text-gray-900 flex items-center gap-1">
                    <TruckIcon className="w-4 h-4" />{selectedAlert.vehicle?.plateNumber || '—'}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">التاريخ</label>
                  <p className="text-gray-900" dir="ltr">
                    {formatTime(selectedAlert.createdAt, timezone, 'YYYY-MM-DD HH:mm:ss')}
                  </p>
                </div>
              </div>
              {selectedAlert.latitude && selectedAlert.longitude && (
                <div>
                  <label className="text-sm text-gray-500">الموقع</label>
                  <p className="text-gray-900 flex items-center gap-1" dir="ltr">
                    <MapPinIcon className="w-4 h-4" />
                    {Number(selectedAlert.latitude).toFixed(6)}, {Number(selectedAlert.longitude).toFixed(6)}
                  </p>
                  <a href={`https://www.google.com/maps?q=${selectedAlert.latitude},${selectedAlert.longitude}`}
                     target="_blank" rel="noopener noreferrer"
                     className="text-primary-600 text-sm hover:underline">
                    فتح في خرائط جوجل
                  </a>
                </div>
              )}
              {/* Metadata now arrives parsed, so every recorded field is shown
                  instead of only a hard-coded `speed` lookup. */}
              {selectedAlert.metadata && Object.keys(selectedAlert.metadata).length > 0 && (
                <div>
                  <label className="text-sm text-gray-500">تفاصيل إضافية</label>
                  <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    {Object.entries(selectedAlert.metadata).map(([k, v]) => (
                      <div key={k} className="contents">
                        <dt className="text-gray-500 truncate">{k}</dt>
                        <dd className="text-gray-900 truncate" dir="ltr">
                          {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-500">الحالة</label>
                  <p className={clsx('font-medium',
                    selectedAlert.isResolved ? 'text-success-600'
                      : selectedAlert.isAcknowledged ? 'text-primary-600' : 'text-warning-600')}>
                    {statusLabel(selectedAlert)}
                  </p>
                </div>
                {selectedAlert.acknowledgedAt && (
                  <div>
                    <label className="text-sm text-gray-500">تاريخ الاستلام</label>
                    <p className="text-gray-900" dir="ltr">
                      {formatTime(selectedAlert.acknowledgedAt, timezone, 'YYYY-MM-DD HH:mm')}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex gap-2 pt-4 border-t">
                {!selectedAlert.isAcknowledged && !selectedAlert.isResolved && (
                  <button
                    onClick={() => acknowledgeMutation.mutate(selectedAlert.id, {
                      // Reflect the server's answer rather than assuming success —
                      // the old modal flipped its own state even when the call failed.
                      onSuccess: (r: any) => setSelectedAlert(r.data.data),
                    })}
                    disabled={acknowledgeMutation.isPending}
                    className="btn btn-primary flex-1 disabled:opacity-50">
                    <EyeIcon className="w-5 h-5 ml-2" />تأكيد الاستلام
                  </button>
                )}
                {!selectedAlert.isResolved && (
                  <button
                    onClick={() => resolveMutation.mutate(selectedAlert.id, {
                      onSuccess: (r: any) => setSelectedAlert(r.data.data),
                    })}
                    disabled={resolveMutation.isPending}
                    className="btn btn-success flex-1 disabled:opacity-50">
                    <CheckCircleIcon className="w-5 h-5 ml-2" />تم الحل
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
